import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { useLinkTo, useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { PromoDetailsPage } from '@/feature/promohub/components/PromoPage';
import { PromoPageContent } from '@/feature/promohub/components/PromoPageContent';
import { usePromotions } from '@/feature/promohub/hooks/use-promotions';
import { usePromoOptIn } from '@/feature/promohub/hooks/use-submissions';
import { useResumeEffect } from '@/hooks/use-resume';
import { PromohubStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { formatPromoExpirationTime } from '@/utils/renderDate';

import { usePromoConfig } from '../hooks/use-promo-details';
import { DEFAULT_PROMO_THEME } from '../hooks/use-promo-theme';

type ScreenProps = NativeStackScreenProps<PromohubStackParamList, 'PromoDetails'>;

export const PromoDetails = (props: ScreenProps) => {
    const { promoId } = props.route.params;
    const { promotions, refetch, isLoading } = usePromotions();
    const promotion = promotions.find(promo => promo.uid === promoId);
    const navigation = useNavigation();
    const linkTo = useLinkTo();

    const theme = promotion?.data.promo_theme.data ?? DEFAULT_PROMO_THEME;
    const customCta = promotion?.data.custom_cta ?? undefined;
    const customCtaPath = promotion?.data.custom_cta_path ?? undefined;
    const showCustomCta = !!customCta && !!customCtaPath;
    const { t } = useTranslation('promohub');
    const { displayCTA, cta, postCTA, title, expirationTime, supportingText } = usePromoConfig(promotion);
    const { optIn, loading } = usePromoOptIn({
        promoId,
        segmented: promotion?.data.segmented ?? false,
        redirectPath: promotion?.data.redirect_path ?? undefined,
        bonusEnginePromoId: promotion?.bonusEngineDetails?.id,
        analyticsEvent: AnalyticsEvent.OPT_IN_PROMO_DETAILS,
    });
    const [isOptedIn, setIsOptedIn] = React.useState(promotion?.optedIn);

    const expirationDays = expirationTime ? formatPromoExpirationTime(expirationTime, t) : undefined;

    const handleCustomCtaPress = () => {
        try {
            linkTo(`/${customCtaPath}`);
        } catch (error) {
            console.error('Error navigating to custom CTA path:', error);
        }
    };

    const handlePress = async () => {
        try {
            optIn();
            setIsOptedIn(true);
        } catch (error) {
            console.error('Error submitting promotion:', error);
        }
    };

    const reload = useCallback(() => {
        refetch();
    }, [refetch]);

    useResumeEffect(reload);

    const { showToast } = useAlerts();

    useEffect(() => {
        if (promoId && !isLoading) {
            const promoData = promotions.find(promo => promo.uid === promoId);
            if (!promoData) {
                showToast({ message: t('promotion_not_valid'), toastType: 'warning' });
                navigation.goBack();
            }
        }
    }, [isLoading, navigation, promoId, promotions, showToast, t]);

    return (
        <SafeAreaProvider style={common.flex}>
            {isLoading || !promotion ? (
                <View style={[common.alignCenter, common.justifyCenter, common.fullHeight]}>
                    <LoadingSpinner />
                </View>
            ) : (
                <PromoDetailsPage
                    theme={theme}
                    pageContent={<PromoPageContent promotion={promotion} />}
                    headerProps={{
                        title,
                        supportingText,
                        expirationTag: expirationDays,
                        onPress: handlePress,
                        isOptedIn,
                        buttonCta: cta,
                        buttonPostCta: postCTA,
                        buttonLoading: loading,
                        showButton: displayCTA,
                        icon: promotion?.data.promotion_details.url ?? '',
                        refferalSummaryCard: null,
                        showNavigationButton: true,
                        showRafHistory: false,
                        customCta,
                        showCustomCta,
                        onCustomCtaPress: handleCustomCtaPress,
                    }}
                />
            )}
        </SafeAreaProvider>
    );
};
